import React, { useState } from 'react';

// Analytics tracking for social media sharing events
const trackSharingEvent = (platform: string, reportId: string, reportLocation?: string) => {
  try {
    // Track with Google Analytics if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'share', {
        method: platform,
        content_type: 'treasure_report',
        content_id: reportId,
        custom_parameter_1: reportLocation || 'unknown_location'
      });
    }

    // Track with custom analytics
    console.log('Share event tracked:', {
      platform,
      reportId,
      location: reportLocation,
      timestamp: new Date().toISOString()
    });

    // Could also send to custom analytics endpoint
    // fetch('/api/analytics/share', { ... })
  } catch (error) {
    console.warn('Failed to track sharing event:', error);
  }
};

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  reportTitle?: string;
  reportImage?: string;
  reportLocation?: string;
}

interface SharePlatform {
  name: string;
  icon: React.ReactNode;
  color: string;
  hoverColor: string;
  action: (url: string, title: string, image?: string) => void;
}

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  reportId,
  reportTitle = 'Check out this treasure on Curbside Quest!',
  reportImage,
  reportLocation
}) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const [shareSuccess, setShareSuccess] = useState<string | null>(null);

  if (!isOpen) return null;

  // Generate share URL using dynamic meta tag endpoint
  const shareUrl = `${window.location.origin}/share/treasure/${reportId}`;
  const shareTitle = reportLocation
    ? `Treasure found in ${reportLocation} - ${reportTitle}`
    : reportTitle;

  // Share platforms configuration
  const platforms: SharePlatform[] = [
    {
      name: 'Facebook',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
      color: 'bg-blue-600',
      hoverColor: 'hover:bg-blue-700',
      action: (url, title) => {
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(title)}`;
        window.open(facebookUrl, '_blank', 'width=600,height=400');
        trackSharingEvent('facebook', reportId, reportLocation);
        setShareSuccess('Facebook');
        setTimeout(() => setShareSuccess(null), 3000);
      }
    },
    {
      name: 'WhatsApp',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
        </svg>
      ),
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600',
      action: (url, title) => {
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`;
        window.open(whatsappUrl, '_blank');
        trackSharingEvent('whatsapp', reportId, reportLocation);
        setShareSuccess('WhatsApp');
        setTimeout(() => setShareSuccess(null), 3000);
      }
    },
    {
      name: 'Instagram',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
      ),
      color: 'bg-gradient-to-r from-purple-500 to-pink-500',
      hoverColor: 'hover:from-purple-600 hover:to-pink-600',
      action: (url, _title) => {
        // Instagram doesn't have direct URL sharing, so we copy to clipboard with instructions
        copyToClipboard(url);
        trackSharingEvent('instagram', reportId, reportLocation);
        setShareSuccess('Instagram');
        setTimeout(() => setShareSuccess(null), 3000);
        alert('Link copied! Open Instagram and paste the link in your story or post.');
      }
    },
    {
      name: 'Twitter/X',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
      color: 'bg-black',
      hoverColor: 'hover:bg-gray-800',
      action: (url, title) => {
        const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        window.open(twitterUrl, '_blank', 'width=600,height=400');
        trackSharingEvent('twitter', reportId, reportLocation);
        setShareSuccess('Twitter');
        setTimeout(() => setShareSuccess(null), 3000);
      }
    }
  ];

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed: ', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleCopyLink = () => {
    copyToClipboard(shareUrl);
    trackSharingEvent('copy_link', reportId, reportLocation);
  };

  const handlePlatformShare = (platform: SharePlatform) => {
    platform.action(shareUrl, shareTitle, reportImage);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full mx-4 overflow-hidden shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-urban-100">
          <h2 id="share-modal-title" className="text-xl font-bold text-urban">Share this treasure</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-urban-50 rounded-full transition-colors duration-200"
            aria-label="Close share modal"
          >
            <svg className="w-5 h-5 text-urban-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Preview */}
          <div className="mb-6 p-4 bg-sky-neutral rounded-xl border border-urban-100">
            <div className="flex items-start gap-3">
              {reportImage && (
                <img
                  src={reportImage}
                  alt="Treasure preview"
                  className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-urban text-sm mb-1 truncate">
                  {shareTitle}
                </h3>
                <p className="text-urban-400 text-xs">
                  Share this treasure with your friends and community
                </p>
              </div>
            </div>
          </div>

          {/* Share Platforms */}
          <div className="space-y-3 mb-6">
            {platforms.map((platform) => (
              <button
                key={platform.name}
                onClick={() => handlePlatformShare(platform)}
                className={`w-full flex items-center gap-4 p-4 rounded-xl text-white font-semibold transition-all duration-200 hover:scale-[1.02] hover:shadow-lg ${platform.color} ${platform.hoverColor}`}
              >
                <div className="flex-shrink-0">
                  {platform.icon}
                </div>
                <span>Share on {platform.name}</span>
                <svg className="w-4 h-4 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
            ))}
          </div>

          {/* Copy Link */}
          <div className="border-t border-urban-100 pt-6">
            <button
              onClick={handleCopyLink}
              className={`w-full flex items-center justify-center gap-3 p-4 rounded-xl font-semibold transition-all duration-200 hover:scale-[1.02] hover:shadow-lg ${copySuccess
                ? 'bg-verge text-white'
                : 'bg-cardboard text-white hover:bg-cardboard-600'
                }`}
            >
              {copySuccess ? (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Link Copied!</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>Copy Link</span>
                </>
              )}
            </button>

            {copySuccess && (
              <p className="text-verge text-sm mt-3 text-center flex items-center justify-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Link copied to clipboard!
              </p>
            )}

            {shareSuccess && (
              <p className="text-verge text-sm mt-3 text-center flex items-center justify-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Successfully shared to {shareSuccess}!
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;